import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/shared/api/client';
import { endpoints } from '@/shared/api/endpoints';
import { getEndpoint } from '@/shared/utils/getUrl';
import type {
  CreateReportDocumentRequest,
  CreateReportSectionRequest,
  ExportReportDocumentRequest,
  ExportedReportResponse,
  PreviewReportDocumentRequest,
  PublishReportDocumentRequest,
  ReportCompanyPresenceResponse,
  ReportDocumentResponse,
  ReportDocumentSectionResponse,
  ReportDocumentSectionsListResponse,
  ReportDocumentVersionsListResponse,
  UpdateReportDocumentSectionRequest,
} from './types';

export const reportDocumentKeys = {
  all: ['reportDocuments'] as const,
  detail: (documentId: string) => ['reportDocuments', 'detail', documentId] as const,
  sections: (documentId: string) => ['reportDocuments', 'sections', documentId] as const,
  versions: (documentId: string) => ['reportDocuments', 'versions', documentId] as const,
  presence: () => ['reportDocuments', 'presence'] as const,
};

export const useReportDocumentQuery = (documentId: string | undefined, enabled: boolean = true) => {
  return useQuery({
    queryKey: reportDocumentKeys.detail(documentId ?? 'missing'),
    queryFn: async () => {
      const response = await apiClient.get<ReportDocumentResponse>(
        getEndpoint(endpoints.reportDocuments.byId, documentId ?? '')
      );
      return response.data;
    },
    enabled: enabled && Boolean(documentId),
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

export const useReportDocumentSectionsQuery = (
  documentId: string | undefined,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: reportDocumentKeys.sections(documentId ?? 'missing'),
    queryFn: async () => {
      const response = await apiClient.get<ReportDocumentSectionsListResponse>(
        getEndpoint(endpoints.reportDocuments.sections, documentId ?? '')
      );
      return response.data;
    },
    enabled: enabled && Boolean(documentId),
    refetchInterval: 15000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

export const useReportDocumentVersionsQuery = (
  documentId: string | undefined,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: reportDocumentKeys.versions(documentId ?? 'missing'),
    queryFn: async () => {
      const response = await apiClient.get<ReportDocumentVersionsListResponse>(
        getEndpoint(endpoints.reportDocuments.versions, documentId ?? '')
      );
      return response.data;
    },
    enabled: enabled && Boolean(documentId),
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

export const useReportCompanyPresenceQuery = (enabled: boolean = true) => {
  return useQuery({
    queryKey: reportDocumentKeys.presence(),
    queryFn: async () => {
      const response = await apiClient.get<ReportCompanyPresenceResponse>(
        getEndpoint(endpoints.reportDocuments.presence),
        { skipTenantForbiddenRedirect: true }
      );
      return response.data;
    },
    enabled,
    refetchInterval: 15000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

export const useCreateReportDocumentMutation = () => {
  return useMutation({
    mutationFn: async (payload: CreateReportDocumentRequest) => {
      const response = await apiClient.post<ReportDocumentResponse>(
        getEndpoint(endpoints.reportDocuments.createFromVulnerabilities),
        payload
      );
      return response.data;
    },
  });
};

export const useResolveReportDocumentFromReportMutation = () => {
  return useMutation({
    mutationFn: async (reportId: string) => {
      const response = await apiClient.post<ReportDocumentResponse>(
        getEndpoint(endpoints.reportDocuments.resolveFromReport, reportId)
      );
      return response.data;
    },
  });
};

export const useUpdateReportDocumentSectionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentId,
      sectionId,
      payload,
    }: {
      documentId: string;
      sectionId: string;
      payload: UpdateReportDocumentSectionRequest;
    }) => {
      const response = await apiClient.patch<ReportDocumentSectionResponse>(
        getEndpoint(endpoints.reportDocuments.updateSectionMaterialized, documentId, sectionId),
        payload
      );
      return response.data;
    },
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: reportDocumentKeys.detail(variables.documentId) }),
        queryClient.invalidateQueries({ queryKey: reportDocumentKeys.sections(variables.documentId) }),
        queryClient.invalidateQueries({ queryKey: reportDocumentKeys.versions(variables.documentId) }),
        queryClient.invalidateQueries({ queryKey: reportDocumentKeys.presence() }),
      ]);
    },
  });
};

export const useCreateReportSectionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentId,
      payload,
    }: {
      documentId: string;
      payload: CreateReportSectionRequest;
    }) => {
      const response = await apiClient.post<ReportDocumentSectionResponse>(
        getEndpoint(endpoints.reportDocuments.createSection, documentId),
        payload
      );
      return response.data;
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: reportDocumentKeys.sections(variables.documentId),
      });
    },
  });
};

export const useDeleteReportSectionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentId,
      sectionId,
    }: {
      documentId: string;
      sectionId: string;
    }) => {
      await apiClient.delete(
        getEndpoint(endpoints.reportDocuments.deleteSection, documentId, sectionId)
      );
      return { documentId, sectionId };
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: reportDocumentKeys.sections(variables.documentId),
      });
    },
  });
};

export const usePublishReportDocumentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentId,
      payload,
    }: {
      documentId: string;
      payload: PublishReportDocumentRequest;
    }) => {
      const response = await apiClient.post(
        getEndpoint(endpoints.reportDocuments.publish, documentId),
        payload
      );
      return response.data;
    },
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: reportDocumentKeys.detail(variables.documentId) }),
        queryClient.invalidateQueries({ queryKey: reportDocumentKeys.sections(variables.documentId) }),
        queryClient.invalidateQueries({ queryKey: reportDocumentKeys.versions(variables.documentId) }),
      ]);
    },
  });
};

export const useExportReportDocumentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentId,
      payload,
    }: {
      documentId: string;
      payload: ExportReportDocumentRequest;
    }) => {
      const response = await apiClient.post<ExportedReportResponse>(
        getEndpoint(endpoints.reportDocuments.export, documentId),
        payload
      );
      return response.data;
    },
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: reportDocumentKeys.detail(variables.documentId) }),
        queryClient.invalidateQueries({ queryKey: ['reports', 'list'] }),
      ]);
    },
  });
};

export const previewReportDocument = async (
  documentId: string,
  payload: PreviewReportDocumentRequest = {}
) => {
  const response = await apiClient.post(
    getEndpoint(endpoints.reportDocuments.preview, documentId),
    payload,
    { responseType: 'blob' }
  );
  return response.data as Blob;
};
